// src/hooks/useGeocodedAccounts.js
import { useEffect, useRef, useState } from "react";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";

const buildAddressFromAccount = (acc = {}) => {
  const street =
    acc.addressStreet ||
    acc.street ||
    (typeof acc.shippingAddress === "string" ? acc.shippingAddress.split(",")[0] : "") ||
    acc.address?.street ||
    "";

  const city =
    acc.addressCity ||
    acc.city ||
    acc.shippingCity ||
    (typeof acc.shippingAddress === "string" ? acc.shippingAddress.split(",")[1] : "") ||
    acc.address?.city ||
    "";

  const state = acc.addressState || acc.state || acc.shippingState || acc.address?.state || "";
  const zip = acc.addressZip || acc.zip || acc.postalCode || acc.address?.zip || "";

  const parts = [street, city, state, zip, "USA"]
    .map((x) => String(x || "").trim())
    .filter(Boolean);

  return parts.join(", ");
};

const asNum = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

const extractCoords = (acc = {}) => {
  const lat =
    asNum(acc.geo?.lat) ??
    asNum(acc.geo?.latitude) ??
    asNum(acc.location?.lat) ??
    asNum(acc.location?.latitude) ??
    asNum(acc.coords?.lat) ??
    asNum(acc.coords?.latitude) ??
    asNum(acc.lat) ??
    asNum(acc.latitude) ??
    null;

  const lng =
    asNum(acc.geo?.lng) ??
    asNum(acc.geo?.longitude) ??
    asNum(acc.location?.lng) ??
    asNum(acc.location?.longitude) ??
    asNum(acc.coords?.lng) ??
    asNum(acc.coords?.longitude) ??
    asNum(acc.lng) ??
    asNum(acc.longitude) ??
    null;

  if (typeof lat === "number" && typeof lng === "number") return { lat, lng };
  return null;
};

const geocodeAddress = async (address, apiKey) => {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
    address
  )}&key=${encodeURIComponent(apiKey)}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Geocoding request failed with status ${res.status}`);

  const data = await res.json();

  if (data.status !== "OK") {
    const msg = data.error_message ? ` (${data.error_message})` : "";
    throw new Error(`Geocoding failed: ${data.status}${msg}`);
  }

  const loc = data.results?.[0]?.geometry?.location;
  if (!loc || typeof loc.lat !== "number" || typeof loc.lng !== "number") return null;

  return { lat: loc.lat, lng: loc.lng };
};

/**
 * @param {string} companyId
 * @param {Array|null} accountsInput
 *    - null means "still loading accounts" (hook will wait)
 *    - [] means "no accounts visible"
 */
const useGeocodedAccounts = (companyId, accountsInput = null) => {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const auth = getAuth();
  const apiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();

  const runIdRef = useRef(0);

  useEffect(() => {
    if (!companyId) {
      setPoints([]);
      setLoading(false);
      setError("");
      return;
    }

    // Wait until parent passes actual accounts array (not null)
    if (accountsInput === null) {
      setLoading(true);
      return;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setPoints([]);
      setLoading(false);
      setError("Not logged in.");
      return;
    }

    if (!apiKey) {
      setPoints([]);
      setLoading(false);
      setError("Missing Google Maps API key (REACT_APP_GOOGLE_MAPS_API_KEY).");
      return;
    }

    let cancelled = false;
    const myRun = ++runIdRef.current;

    const run = async () => {
      setLoading(true);
      setError("");

      try {
        const accounts = Array.isArray(accountsInput) ? accountsInput : [];
        if (!accounts.length) {
          setPoints([]);
          setLoading(false);
          return;
        }

        const out = [];
        let geocodeDenied = false;
        let geocodeFailures = 0;

        const queue = accounts.slice();
        let idx = 0;

        // 4 concurrent workers (fast, but not insane)
        const workers = Array.from({ length: 4 }).map(async () => {
          while (!cancelled && myRun === runIdRef.current && idx < queue.length) {
            const acc = queue[idx++];
            if (!acc?.id) continue;

            const coordsExisting = extractCoords(acc);
            const fullAddress = buildAddressFromAccount(acc);

            // If stored coords exist, use immediately
            if (coordsExisting) {
              out.push({
                id: acc.id,
                name: acc.name || acc.businessName || "Unnamed account",
                city: acc.addressCity || acc.city || acc.address?.city || null,
                state: acc.addressState || acc.state || acc.address?.state || null,
                zip: acc.addressZip || acc.zip || acc.address?.zip || null,
                address: fullAddress || null,
                lat: coordsExisting.lat,
                lng: coordsExisting.lng,
              });
              continue;
            }

            // No coords + no address = skip
            if (!fullAddress) continue;

            try {
              const coords = await geocodeAddress(fullAddress, apiKey);
              if (!coords) continue;

              out.push({
                id: acc.id,
                name: acc.name || acc.businessName || "Unnamed account",
                city: acc.addressCity || acc.city || acc.address?.city || null,
                state: acc.addressState || acc.state || acc.address?.state || null,
                zip: acc.addressZip || acc.zip || acc.address?.zip || null,
                address: fullAddress,
                lat: coords.lat,
                lng: coords.lng,
              });

              // Cache coords back into Firestore so next load is instant
              try {
                await updateDoc(doc(db, "companies", companyId, "accounts", acc.id), {
                  geo: { lat: coords.lat, lng: coords.lng },
                  geoUpdatedAt: serverTimestamp(),
                });
              } catch (e) {
                // Ignore write errors (rules might prevent updates)
              }
            } catch (geoErr) {
              geocodeFailures += 1;
              const msg = String(geoErr?.message || "");
              if (msg.includes("REQUEST_DENIED")) geocodeDenied = true;
              console.warn("[useGeocodedAccounts] Geocode failed:", acc.id, geoErr);
            }
          }
        });

        await Promise.all(workers);

        if (cancelled || myRun !== runIdRef.current) return;

        if (!out.length && geocodeDenied) {
          setError(
            "Geocoding is being denied (REQUEST_DENIED). Enable the Geocoding API for this key + billing, or store geo {lat,lng} on accounts."
          );
        } else if (!out.length && geocodeFailures > 0) {
          setError("Geocoding failed for all accounts. Check console for Google error messages.");
        } else {
          setError("");
        }

        setPoints(out);
      } catch (err) {
        console.error("[useGeocodedAccounts] Error:", err);
        setError(err?.message || "Failed to load account locations.");
        setPoints([]);
      } finally {
        setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [companyId, accountsInput, auth, apiKey]);

  return { points, loading, error };
};

export default useGeocodedAccounts;
export { useGeocodedAccounts };
